import * as THREE from 'three';
import { G, M, TINT, HP, K_BEV, glowMat, mesh, bevel, rib, seam, cable, joint, ram, mergeBones, stages, boneG } from './kit';
/* ============================================================
   WELTACHSE 世界軸 —— 最終・框不下
   ============================================================

   剪影：48 單位的軸・看不到頂端
   階段：'P1 只看得到最底下兩節','P2 下降十四單位','P3 再下降十四・頂端第一次進入畫面'

   由 `tools/port-bosses.mjs` 從 `enemy_sample_ani.html` 機械移植，
   **不要手改這個檔**：要改造型請改原型再重跑移植器，否則 1:1 就斷了。
   驗證：`node tools/port-bosses.mjs --check`。
   ⚠️ 比對只守得住數字與字串，**動畫對不對只能實看**（圖鑑的開發模式）。 */


/* ── BOSS-27 WELTACHSE 世界軸 ── 48 單位高。**相機框不下，這是刻意的。**
   `fit: 0.34` 讓取景只涵蓋大約 19 單位 —— 你永遠看不到它的頂端。
   每過一階段它下降九單位，於是原本在畫面外的區段降進視野：
   你不是把它打小，你是把它**拉下來**。
   最後一階段它的頂端才第一次進入畫面，而那不是頭，是另一段軸。 */
var WAN = 6;
export function buildWeltachse(){
  var GLV = glowMat(TINT.vio.hex, 2.5), GLA = glowMat(TINT.amb.hex, 2.2);
  var root = new THREE.Group(), segs = [], i, j;
  var col = boneG(root, 0, 0, 0);
  for (i = 0; i < WAN; i++){
    var sg = boneG(col, 0, 2.60 + i*8.20, 0);
    var k = 1 - i*0.055;
    sg.add(mesh(G.bev, M.armorF, [4.40*K_BEV*k, 7.60, 4.40*K_BEV*k], [0, 0, 0]));
    sg.add(mesh(G.tor, M.trim,  [6.20*k, 6.20*k, 0.60], [0, 3.60, 0], [HP,0,0]));
    sg.add(joint(0.70*k, [0, -3.80, 0], true));            // 節間關節：軸端外露
    sg.add(ram([ 1.80*k, -3.20, 1.10], 2.20, 0.30, 0.24, [-0.12,0,0]));
    sg.add(ram([-1.80*k, -3.20, 1.10], 2.20, 0.30, 0.24, [-0.12,0,0]));
    sg.add(seam(3.20*k, 0.16, 0.30, [0, 2.60, 1.90]));
    sg.add(seam(3.20*k, 0.16, 0.30, [0,-2.60, 1.90]));
    sg.add(rib(M.armorF, 0.26, 7.00, 0.26, [ 2.30*k, 0, 1.30]));
    sg.add(rib(M.armorF, 0.26, 7.00, 0.26, [-2.30*k, 0, 1.30]));
    /* 每節掛一圈自轉環 —— 六節的轉向交錯，讓整根軸讀起來是活的 */
    var rg = boneG(sg, 0, 0, 0);
    rg.add(mesh(G.tor, M.dark, [9.40*k, 9.40*k, 0.55], [0,0,0], [HP,0,0]));
    for (j = 0; j < 4; j++){
      var a = j*Math.PI * 2/4 + i*0.4;
      rg.add(mesh(G.box, M.armorF, [1.40,1.10,2.60],
        [Math.sin(a)*4.40*k, 0, Math.cos(a)*4.40*k], [0,-a,0]));
    }
    var gm = mesh(G.tor, i % 2 ? GLV : GLA, [10.2*k, 10.2*k, 0.26], [0,0,0], [HP,0,0]);
    gm.userData.noMerge = 1; rg.add(gm);
    segs.push({sg:sg, rg:rg, gm:gm, i:i, k:k});
  }
  var base = boneG(root, 0, 0.80, 0);
  base.add(bevel(M.dark, 7.00, 1.60, 7.00, [0,0,0]));
  base.add(cable([2.40, 0.60, 1.90], 1.80));
  for (i = 0; i < 5; i++){
    var b = i*Math.PI * 2/5 + 0.3;                                     // 五 —— 又是鋪不滿的數
    base.add(mesh(G.cone, M.armorF, [1.60, 4.40, 1.60],
      [Math.sin(b)*3.40, -0.20, Math.cos(b)*3.40],
      [0.40*Math.cos(b) + Math.PI, 0, -0.40*Math.sin(b)]));
  }
  var vd = mesh(G.oct, M.void, [3.60, 3.60, 3.60], [0, 2.20, 0]);
  vd.userData.noMerge = 1; base.add(vd);
  root.userData.rig = {col:col, segs:segs, base:base, vd:vd, GLV:GLV, GLA:GLA};
  return mergeBones(root);
}
export function updWeltachse(rig, o, dt){
  stages(rig, o, dt, 1.1);
  var s1 = rig.s1, s2 = rig.s2, s3 = rig.s3, i, S;
  /* 下降：每階段九單位。畫面外的東西被拉進來。 */
  rig.col.position.y = -(14.0*s1 + 14.0*s2) + 0.40*Math.sin(o.t*0.5);
  rig.col.rotation.y += dt*(0.08 + 0.20*s1 + 0.30*s2);
  for (i = 0; i < WAN; i++){
    S = rig.segs[i];
    S.rg.rotation.z += dt*(0.35 + 0.18*i)*(i % 2 ? 1 : -1)*(1 + 1.2*s1 + 2.4*s2);
    S.sg.rotation.y = 0.14*Math.sin(o.t*0.4 + i*1.3)*(1 + s2);
    S.sg.position.x = 0.30*Math.sin(o.t*0.6 + i*0.9)*s2;
    S.gm.scale.set(10.2*S.k*(1 + 0.06*Math.sin(o.t*2 + i)), 10.2*S.k, 0.26);
  }
  rig.base.position.y = 0.80 - 0.30*s2;
  rig.vd.scale.setScalar(1 + 0.7*s1 + 1.1*s2);
  rig.GLV.emissiveIntensity = 2.5 + 2.4*o.tel + 6*o.atk*(1-o.atk) + 1.6*s2;
  rig.GLA.emissiveIntensity = 2.2 + 1.4*s1 + 1.8*s2 + 2.2*o.tel;
}